// ── Icon normalisation ────────────────────────────────────────────────────────
// User-created icons are drawn in PowerPoint and exported as PNGs. To keep the
// library consistent, an uploaded image is scaled-to-fit and centered inside the
// standard rounded-rect frame (vsiconblank.png) and saved at the standard size, so
// every user-added icon matches the built-in ones regardless of what was pasted in.
//
// Extracted from IconPicker so the Icons ADMIN screen frames identically. Two
// copies of this would drift, and the drift would be invisible: icons added from
// the picker and icons added from the admin screen would simply stop matching
// each other, with nothing to indicate which code path produced which.
import { buildIconPath } from '@/lib/imagePaths'
import { toAssetBaseName } from '@/lib/assetFilename'

export const FRAME_FILENAME = 'vsiconblank.png'
const FALLBACK_SIZE = 788
const INNER_PADDING = 0.05 // fraction of the canvas kept clear around the logo (minimal margin)

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error('Failed to load image'))
        img.src = src
    })
}

// The frame is served from the app's own origin, so no crossOrigin is needed and the
// canvas stays untainted (toBlob works). Cached for the session.
let framePromise: Promise<HTMLImageElement | null> | null = null
function loadFrame(): Promise<HTMLImageElement | null> {
    if (!framePromise) framePromise = loadImage(buildIconPath(FRAME_FILENAME)).catch(() => null)
    return framePromise
}

export async function composeIconOnFrame(file: File): Promise<{ blob: Blob; dataUrl: string }> {
    const objectUrl = URL.createObjectURL(file)
    try {
        const [logo, frame] = await Promise.all([loadImage(objectUrl), loadFrame()])
        const W = frame?.naturalWidth || FALLBACK_SIZE
        const H = frame?.naturalHeight || FALLBACK_SIZE
        const canvas = document.createElement('canvas')
        canvas.width = W
        canvas.height = H
        const ctx = canvas.getContext('2d')
        if (!ctx) throw new Error('Canvas not supported')
        if (frame) ctx.drawImage(frame, 0, 0, W, H)
        const innerW = W * (1 - INNER_PADDING * 2)
        const innerH = H * (1 - INNER_PADDING * 2)
        const scale = Math.min(innerW / logo.naturalWidth, innerH / logo.naturalHeight)
        const dw = logo.naturalWidth * scale
        const dh = logo.naturalHeight * scale
        ctx.drawImage(logo, (W - dw) / 2, (H - dh) / 2, dw, dh)
        const blob = await new Promise<Blob>((resolve, reject) =>
            canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Failed to render icon'))), 'image/png')
        )
        return { blob, dataUrl: canvas.toDataURL('image/png') }
    } finally {
        URL.revokeObjectURL(objectUrl)
    }
}

// Icons are always framed to PNG, so the extension is fixed here. The BASE name
// comes from the shared rule so icons and shapes cannot answer differently.
export function toIconFilename(name: string, fallback: string): string {
    return `${toAssetBaseName(name, fallback)}.png`
}
